import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Search, Bookmark, FileText, Clock, ChevronRight, Building2, User, Sparkles, BriefcaseBusiness, LayoutList } from 'lucide-react';

export default function CompanyDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/company/login'); };

  const isPending = user?.companyStatus === 'PENDING';
  const orgLabel = user?.organizationType === 'HOSPITAL' ? 'Hospital' : 'Company';

  // Fallback display values in case backend hasn't been updated yet
  const displayName = user?.companyName || '—';
  const displayPerson = user?.contactPersonName || '—';
  const displayEmail = user?.contactEmail || user?.contactPhone || '—';

  const cards = [
    { icon: Search,          color: 'teal',    title: 'Search Staff',        desc: 'Browse verified healthcare professionals',  path: '/company/search',          disabled: isPending },
    { icon: BriefcaseBusiness,color:'orange',  title: 'Post Opportunity',    desc: 'Post a job and let staff apply',            path: '/company/opportunities',   disabled: isPending },
    { icon: Bookmark,        color: 'indigo',  title: 'My Shortlist',        desc: 'Candidates you have bookmarked',            path: '/company/shortlist',        disabled: isPending },
    { icon: FileText,        color: 'emerald', title: 'Hire Requests',       desc: 'Track your direct hire requests',           path: '/company/hire-requests',    disabled: isPending },
    { icon: LayoutList,      color: 'cyan',    title: 'My Placements',       desc: 'View active and past placements',           path: '/company/placements',       disabled: isPending },
    { icon: Building2,       color: 'blue',    title: 'My Facilities',       desc: 'Manage your facility profiles',             path: '/company/facilities',       disabled: isPending },
    { icon: Sparkles,        color: 'amber',   title: 'AI Staffing Plan',    desc: 'Generate smart staffing recommendations',   path: '/company/staffing-plans',   disabled: isPending },
    { icon: User,            color: 'violet',  title: 'Company Profile',     desc: 'View and edit your company details',        path: '/company/profile',          disabled: false },
  ];

  const colorMap = {
    teal:    { bg: 'bg-teal-50',    icon: 'text-teal-600'    },
    indigo:  { bg: 'bg-indigo-50',  icon: 'text-indigo-600'  },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
    violet:  { bg: 'bg-violet-50',  icon: 'text-violet-600'  },
    blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600'    },
    amber:   { bg: 'bg-amber-50',   icon: 'text-amber-600'   },
    orange:  { bg: 'bg-orange-50',  icon: 'text-orange-600'  },
    cyan:    { bg: 'bg-cyan-50',    icon: 'text-cyan-600'    },
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── NAVBAR ──────────────────────────────────────────────── */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Left: Logo + org name */}
          <div className="flex items-center gap-4">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-8 w-auto" />
            <div className="w-px h-6 bg-gray-200" />
            <div>
              <p className="text-sm font-bold text-slate-800 leading-tight">{displayName}</p>
              <p className="text-xs text-gray-400">{orgLabel} Portal</p>
            </div>
          </div>

          {/* Right: Person + logout */}
          <div className="flex items-center gap-5">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-700 leading-tight">{displayPerson}</p>
              <p className="text-xs text-gray-400">{user?.contactPhone || ''}</p>
            </div>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500
                hover:text-red-700 transition">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── HERO HEADER ─────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-slate-900 to-teal-900 rounded-2xl p-8 mb-8 relative overflow-hidden">
          {/* Glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 85% 50%, rgba(20,184,166,0.25) 0%, transparent 60%)' }} />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.15em] mb-2">
                {orgLabel} Dashboard
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight">
                {displayName}
              </h1>
              <p className="text-slate-400 text-sm">
                {displayPerson}
                {displayEmail && displayEmail !== '—' && (
                  <span className="text-slate-500"> · {displayEmail}</span>
                )}
              </p>
            </div>

            {/* Powered by */}
            <div className="shrink-0 text-right hidden sm:block">
              <p className="text-slate-600 text-xs mb-1.5">Powered by</p>
              <img src="/logo-white.png" alt="ShramSena"
                className="h-8 w-auto opacity-50" />
            </div>
          </div>

          {/* Pending banner */}
          {isPending && (
            <div className="relative mt-5 flex items-start gap-3
              bg-yellow-500/15 border border-yellow-500/25 rounded-xl px-4 py-3">
              <Clock className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-yellow-200 text-sm leading-relaxed">
                <span className="font-semibold">Account Pending Approval</span> — Our admin
                team is reviewing your registration. All features will unlock once approved.
              </p>
            </div>
          )}
        </div>

        {/* ── QUICK ACTIONS ───────────────────────────────────────── */}
        <h2 className="text-base font-bold text-slate-700 mb-4 uppercase tracking-wide">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card) => {
            const Icon = card.icon;
            const c = colorMap[card.color];
            return (
              <div key={card.title}
                onClick={() => !card.disabled && navigate(card.path)}
                className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-6
                  flex flex-col gap-4 transition-all
                  ${card.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-gray-200'}`}>

                <div className={`w-12 h-12 ${c.bg} rounded-xl flex items-center justify-center`}>
                  <Icon className={`h-6 w-6 ${c.icon}`} />
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 mb-1">{card.title}</h3>
                  <p className="text-gray-400 text-sm">{card.desc}</p>
                  {card.disabled && (
                    <p className="text-xs text-amber-500 font-semibold mt-2">
                      Available after approval
                    </p>
                  )}
                </div>

                {!card.disabled && (
                  <div className="flex items-center gap-1 text-teal-600 text-sm font-semibold">
                    Open <ChevronRight className="h-4 w-4" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}